/**
 * The single HTTP client. It owns one job beyond fetch: refreshing an expired
 * access token exactly once, with concurrent callers sharing that one refresh
 * rather than stampeding (SRS §5 — a reused refresh token revokes the family,
 * so a stampede would log the user out).
 */

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, string[]> | Record<string, unknown>;
}

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly error: ApiError,
  ) {
    super(error.message);
    this.name = 'ApiRequestError';
  }

  /** Field errors from a 422, shaped for react-hook-form. */
  get fieldErrors(): Record<string, string[]> {
    const details = this.error.details;
    if (!details || typeof details !== 'object') return {};
    const out: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(details)) {
      if (Array.isArray(v) && v.every((x) => typeof x === 'string')) out[k] = v;
    }
    return out;
  }
}

type Scope = 'company' | 'platform';

interface TokenStore {
  accessToken: string | null;
  scope: Scope;
}

// The access token lives in memory only, never localStorage: an XSS that can
// read storage can then replay the token (SRS §5 — "Access JWT, in memory").
const store: TokenStore = { accessToken: null, scope: 'company' };

export function setAccessToken(token: string | null, scope: Scope = 'company'): void {
  store.accessToken = token;
  store.scope = scope;
}

export function getAccessToken(): string | null {
  return store.accessToken;
}

function basePath(scope: Scope): string {
  return scope === 'platform' ? '/api/platform/v1' : '/api/v1';
}

let refreshInFlight: Promise<boolean> | null = null;

async function refreshOnce(scope: Scope): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${basePath(scope)}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return false;
      const body = (await res.json()) as { data: { accessToken: string } };
      store.accessToken = body.data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      // Clear on the next tick so every caller awaiting this attempt sees the
      // same answer before a new attempt can start.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();
  return refreshInFlight;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  scope?: Scope;
  /** Set for login/activate/reset, which must not try to refresh on a 401. */
  anonymous?: boolean;
  signal?: AbortSignal;
  /** Extra request headers, e.g. Idempotency-Key (SRS §17). */
  headers?: Record<string, string>;
}

/** Sends the request, refreshing an expired access token exactly once. */
async function send(path: string, options: RequestOptions): Promise<Response> {
  const scope = options.scope ?? store.scope;
  const url = path.startsWith('/api') ? path : `${basePath(scope)}${path}`;
  const isForm = typeof FormData !== 'undefined' && options.body instanceof FormData;

  const once = async (): Promise<Response> =>
    fetch(url, {
      method: options.method ?? 'GET',
      credentials: 'include',
      signal: options.signal,
      headers: {
        ...options.headers,
        // A FormData body sets its own multipart boundary.
        ...(options.body === undefined || isForm ? {} : { 'Content-Type': 'application/json' }),
        ...(store.accessToken && !options.anonymous
          ? { Authorization: `Bearer ${store.accessToken}` }
          : {}),
      },
      body:
        options.body === undefined
          ? undefined
          : isForm
            ? (options.body as FormData)
            : JSON.stringify(options.body),
    });

  let res = await once();
  if (res.status === 401 && !options.anonymous) {
    const refreshed = await refreshOnce(scope);
    if (refreshed) res = await once();
  }
  return res;
}

async function failure(res: Response): Promise<ApiRequestError> {
  const text = await res.text();
  let error: ApiError | undefined;
  try {
    error = text ? (JSON.parse(text) as { error?: ApiError }).error : undefined;
  } catch {
    error = undefined;
  }
  return new ApiRequestError(
    res.status,
    error ?? { code: 'UNKNOWN', message: `Request failed with ${res.status}` },
  );
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const res = await send(path, options);
  if (res.status === 204) return undefined as T;
  if (!res.ok) throw await failure(res);
  const text = await res.text();
  const payload = text ? (JSON.parse(text) as { data?: T }) : {};
  return payload.data as T;
}

/** For list endpoints whose `meta` (cursor, total) matters. */
export async function apiPage<T>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T; meta: Record<string, unknown> }> {
  const res = await send(path, options);
  if (!res.ok) throw await failure(res);
  return (await res.json()) as { data: T; meta: Record<string, unknown> };
}

/** Fetch a file with the session and hand it to the browser as a download. */
export async function download(
  path: string,
  filename: string,
  options: RequestOptions = {},
): Promise<void> {
  const res = await send(path, options);
  if (!res.ok) throw await failure(res);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
